import { Breakpoint, Typography } from '@altibox/design-system-component-lib';
import React, { FunctionComponent } from 'react';
import { useSelector } from 'react-redux';
import { Navigate } from 'react-router-dom';

import { InformationCard } from 'app/components/information-card';
import { i18n } from 'app/i18n';
import {
    selectOrderConfirmation,
    selectSelectedAmountOfUnitsToPurchase,
    selectSelectedServiceLocationOrThrow,
    selectSelectedUserDetailsOrThrow,
    selectShoppingCartOrThrow,
} from 'app/pages/order/redux/order-selector';
import { OrderInformationUtils } from 'app/pages/order/utils/order-information-utils';
import { DateUtils } from 'app/utils/date-utils';
import { I18nUtils } from 'app/utils/i18n-utils';
import { OrderStaticData } from 'app/static-data/order-static-data';
import { NavigationService } from 'app/services/navigation-service';

import {
    StyledDetailsRow,
    StyledDetailsTitle,
    StyledOrderDetails,
    StyledProducts,
    StyledProductText,
    StyledProductTitle,
    StyledText,
    StyledTitle,
} from './order-details.styled';

export const OrderDetails: FunctionComponent = () => {
    const userDetails = useSelector(selectSelectedUserDetailsOrThrow);
    const serviceLocation = useSelector(selectSelectedServiceLocationOrThrow);
    const numberOfWifiUnits = useSelector(selectSelectedAmountOfUnitsToPurchase);
    const shoppingCart = useSelector(selectShoppingCartOrThrow);
    const { homeGatewayUpgrade, freeUnit, isUpgradeUnitAdded } = shoppingCart;
    const isIncludedOrUpgradeUnitAdded = isUpgradeUnitAdded || freeUnit;

    const orderConfirmation = useSelector(selectOrderConfirmation);

    if (!orderConfirmation) {
        return <Navigate to={NavigationService.ERROR} replace={true} />;
    }

    const { orderNumber, requestedCompletionDate } = orderConfirmation;

    const renderOrderDetailsRow = (label: string, value: string, isValueSensitive?: boolean): React.ReactNode => (
        <StyledDetailsRow>
            <StyledDetailsTitle component="p" variant="paragraph3" maxBreakpoint={Breakpoint.TABLET}>
                {label}
            </StyledDetailsTitle>
            <StyledText
                component="p"
                variant="paragraph3"
                maxBreakpoint={Breakpoint.TABLET}
                data-hj-suppress={isValueSensitive}
            >
                {value}
            </StyledText>
        </StyledDetailsRow>
    );

    const renderProductText = (text: React.ReactNode): React.ReactNode => (
        <StyledProductText component="p" variant="paragraph3" maxBreakpoint={Breakpoint.TABLET}>
            {text}
        </StyledProductText>
    );

    const getDescriptionText = (): string => {
        if (isIncludedOrUpgradeUnitAdded) {
            return i18n.page.success.orderDetails.descriptionWithFreeUnit;
        }

        if (homeGatewayUpgrade) {
            return i18n.page.success.orderDetails.descriptionWithHomeGatewayUpgrade;
        }

        return i18n.page.success.orderDetails.description;
    };

    return (
        <InformationCard $showBordersForMobile={true}>
            <StyledTitle component="h2" variant="paragraph1">
                {i18n.page.success.orderDetails.title}
            </StyledTitle>
            <Typography component="p" variant="paragraph3">
                {I18nUtils.message(getDescriptionText(), {
                    quantity: numberOfWifiUnits,
                })}
            </Typography>
            <StyledOrderDetails>
                {orderNumber && renderOrderDetailsRow(i18n.page.success.orderDetails.orderNumber, orderNumber, true)}
                {renderOrderDetailsRow(
                    i18n.page.success.orderDetails.customer,
                    OrderInformationUtils.getFullName(userDetails),
                    true,
                )}
                {renderOrderDetailsRow(
                    i18n.page.success.orderDetails.paymentMethodTitle,
                    i18n.page.success.orderDetails.paymentMethod,
                )}
                {requestedCompletionDate &&
                    renderOrderDetailsRow(
                        i18n.page.success.orderDetails.date,
                        DateUtils.toShortDate(requestedCompletionDate),
                    )}
                {renderOrderDetailsRow(
                    i18n.page.success.orderDetails.address,
                    OrderInformationUtils.getFullAddress(serviceLocation),
                    true,
                )}
            </StyledOrderDetails>
            <StyledDetailsRow>
                <StyledProductTitle component="p" variant="paragraph3" maxBreakpoint={Breakpoint.TABLET}>
                    {i18n.page.success.orderDetails.products}
                </StyledProductTitle>
                <StyledProducts>
                    {renderProductText(I18nUtils.richMessage(i18n.page.success.amount, { amount: numberOfWifiUnits }))}
                    {renderProductText(i18n.page.success.orderDetails.productName)}
                    {isIncludedOrUpgradeUnitAdded && (
                        <>
                            {renderProductText(
                                I18nUtils.richMessage(i18n.page.success.amount, {
                                    amount: 1,
                                }),
                            )}
                            {renderProductText(i18n.page.success.orderDetails.freeUnit)}
                        </>
                    )}
                    {homeGatewayUpgrade && (
                        <>
                            {renderProductText(
                                I18nUtils.richMessage(i18n.page.success.amount, {
                                    amount: OrderStaticData.HOME_GATEWAY_UPGRADE_AMOUNT,
                                }),
                            )}
                            {renderProductText(homeGatewayUpgrade.displayName)}
                        </>
                    )}
                </StyledProducts>
            </StyledDetailsRow>
        </InformationCard>
    );
};
